import { getInstanceDefinition } from '../cards/cardLookup';
import { isHeroLike, hasTraitPresent } from '../cards/effectHelpers';
import { hasDarknessAtRank } from '../combat/lightDarkness';

// Monsters that can't legally be chosen as a fight target at all unless a
// condition on the player's hand holds. Shared by actions/dungeonActions.js
// (enforces it) and ai/heuristics/dungeonHeuristics.js (must not even
// propose fighting an ineligible monster - see the "never proposes an
// illegal action" self-play test). Each check receives (state, player,
// hallRankIndex) - hallRankIndex is only used by rank-aware entries
// (Penumbra Fiend); every other entry ignores its 3rd argument.
export const FIGHT_ELIGIBILITY = {
  'monster-necrophidius': (state, player) => player.hand
    .filter((id) => isHeroLike(getInstanceDefinition(state, id))).length >= 2,
  'monster-ossuous': (state, player) => player.hand.some((id) => {
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) && (def.level || 0) >= 1;
  }),
  // Caverns of Bane, Legion General: "Cannot be fought unless 2 or more
  // monsters are present." "Present" = monster-type cards in hand (same
  // convention as Veteran Trainer's Village ability).
  'monster-legion-general': (state, player) => player.hand
    .filter((id) => getInstanceDefinition(state, id).type === 'monster').length >= 2,
  // Root of Corruption, King Caelan: "You cannot select this monster to
  // fight if you have used a Dungeon ability on a hero or villager."
  // usedAbilityInstanceIds entries are `${instanceId}:${timing}:${abilityIndex}` -
  // instanceIds themselves never contain ':', so splitting on it is safe.
  'bearer-king-caelan': (state, player) => !player.turnFlags.usedAbilityInstanceIds.some((key) => {
    const [usedInstanceId, timing] = key.split(':');
    if (timing !== 'dungeon') return false;
    const def = getInstanceDefinition(state, usedInstanceId);
    return isHeroLike(def) || def.traits.includes('Villager');
  }),
  // Root of Corruption, War Incarnate: "You can only select this monster to
  // fight if you have an equipped hero."
  'monster-war-incarnate': (state, player) => Object.keys(player.equippedWeapons).length > 0,
  // Numenera, Penumbra Fiend: "You cannot select this monster to fight if
  // there is Darkness in this rank." Rank-aware, unlike every other entry
  // above.
  'monster-penumbra-fiend': (state, player, hallRankIndex) => hallRankIndex === undefined || !hasDarknessAtRank(state, player.id, hallRankIndex),
  // Root of Corruption, Minion of Horror: identical printed text/shape to
  // Penumbra Fiend above ("You cannot select this monster to fight if there
  // is Darkness in this rank.") - missing from source data entirely until
  // an audit pass caught it via a direct card-scan read (see
  // docs/expansion-audit.md). Reuses the same check.
  'monster-minion-of-horror': (state, player, hallRankIndex) => hallRankIndex === undefined || !hasDarknessAtRank(state, player.id, hallRankIndex),
};

// Numenera, Terror Bird: "If a Doomladen is present (in your hand), you
// must select a Terror Bird to fight." Unlike every entry in
// FIGHT_ELIGIBILITY above (which only gates whether THAT SPECIFIC monster
// can be selected), this blocks selecting anything ELSE - checked inside
// isMonsterFightEligible itself (the single choke point every hall-monster
// selection already passes through, for both actions/dungeonActions.js's
// enforcement and the AI's own "don't even propose it" heuristic) rather
// than a new obligation-tracking mechanism, since it's a live condition
// (hand contents + hall contents at the moment of choosing), not a
// triggered/cleared state like Troll Skullcracker's own
// forcedFightObligation.
function mustFightTerrorBird(state, player) {
  return hasTraitPresent(state, player.id, 'Doomladen')
    && state.dungeonHall.some((id) => id && getInstanceDefinition(state, id).id === 'monster-terror-bird');
}

export function isMonsterFightEligible(state, player, monsterDef, hallRankIndex) {
  if (mustFightTerrorBird(state, player) && monsterDef.id !== 'monster-terror-bird') return false;
  const check = FIGHT_ELIGIBILITY[monsterDef.id];
  return !check || check(state, player, hallRankIndex);
}

// Whether the player currently has *any* legal fight target at all - used to
// scope the "you must choose a monster to fight" rule (rulebook p.18): that
// obligation can only apply if there's actually someone fightable. An empty
// hall (dungeon deck exhausted at end-game) or a hall full of temporarily-
// ineligible monsters (Necrophidius/Ossuous) both mean nobody can be forced
// to fight - there's no legal way to comply.
export function hasAnyFightableMonster(state, player) {
  return state.dungeonHall.some((instanceId, hallRankIndex) => {
    if (!instanceId) return false;
    return isMonsterFightEligible(state, player, getInstanceDefinition(state, instanceId), hallRankIndex);
  });
}
